/*
 * live-preview-cart.js — renders the personalised cover preview wherever a
 * cart line item appears: the cart page, the cart drawer, and the add-to-cart
 * notification (all fed the same descriptor the PDP captured).
 *
 * The Liquid snippet draws the base cover <img> natively (so it shows without JS
 * and is sized by the theme); this script overlays the customer's text from
 * properties[_lp_preview] (or legacy _preview) — same box %, colour, alignment,
 * font (loaded from font.url) and the FINAL sizePct as a CEILING, auto-fitted to
 * the box exactly like the PDP does, so a smaller render never clips. No canvas,
 * no hosting.
 *
 * Loaded GLOBALLY (layout/theme.liquid) because the drawer/notification inject
 * their HTML via the Section Rendering API (innerHTML), which does not execute
 * inline <script>s — so the observer must already be on the page.
 *
 * Click a preview to zoom it (larger modal) — handy to check the personalisation.
 */
(function () {
  var STYLE_ID = 'lp-cart-style';
  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent =
      // display/align-items !important: a theme rule can out-specify our class and
      // flatten the flex box to block, which pins the text to the top instead of
      // centring it. Seen on the PDP with Dawn's .media > :not(...) rule.
      '.lp-cart-overlay{position:absolute;overflow:hidden;display:flex !important;align-items:center !important;pointer-events:none;z-index:2;}' +
      // break-word, not anywhere — see snippets/live-preview.liquid. `anywhere`
      // splits a word that would have fitted alone; that is the defect order
      // 7673 was printed with, and this renderer has to agree with the PDP.
      '.lp-cart-text{width:100%;white-space:pre-wrap;overflow-wrap:break-word;line-height:1.15;margin:0;}' +
      '.lp-cart-zoom{cursor:zoom-in;}' +
      '.lp-cart-modal{position:fixed;inset:0;background:rgba(0,0,0,.72);display:flex;align-items:center;justify-content:center;z-index:10000;padding:20px;cursor:zoom-out;}' +
      '.lp-cart-modal-box{position:relative;width:min(90vw,600px);}' +
      '.lp-cart-modal-box img{display:block;width:100%;height:auto;border-radius:6px;}' +
      '.lp-cart-modal-close{position:absolute;top:-14px;right:-14px;width:34px;height:34px;border-radius:50%;border:none;background:#fff;color:#111;font-size:20px;line-height:1;cursor:pointer;box-shadow:0 2px 8px rgba(0,0,0,.3);}';
    document.head.appendChild(s);
  }

  // family -> Promise that settles when the face is genuinely usable.
  var fontPromises = {};

  // Load a customer font and return a promise you can re-fit on.
  //
  // Prefer the FontFace API: it hands back a REAL load promise. Injecting a
  // <style> and calling document.fonts.load() straight after does NOT work —
  // the face isn't in document.fonts until the next style recalc, so the call
  // resolves immediately and the fit runs against the serif fallback. That is
  // what made a cold (uncached) load render the text ~1/3 too small.
  function loadFont(family, url) {
    if (!family || !url) return Promise.resolve();
    if (fontPromises[family]) return fontPromises[family];

    var p = null;
    if (window.FontFace && document.fonts && document.fonts.add) {
      try {
        var face = new FontFace(family, 'url("' + url + '")', {
          weight: '100 900',
          style: 'normal',
          display: 'swap',
        });
        document.fonts.add(face);
        p = face.load().then(noop, noop);
      } catch (e) {
        p = null;
      }
    }
    if (!p) {
      // Fallback for browsers without the FontFace API: @font-face + best effort.
      var id = 'lpcf-' + family.replace(/[^a-z0-9]+/gi, '');
      if (!document.getElementById(id)) {
        var st = document.createElement('style');
        st.id = id;
        st.textContent =
          '@font-face{font-family:"' + family + '";src:url("' + url + '");font-weight:100 900;font-style:normal;font-display:swap;}';
        document.head.appendChild(st);
      }
      p = document.fonts && document.fonts.load
        ? document.fonts.load('40px "' + family + '"').then(noop, noop)
        : Promise.resolve();
    }
    fontPromises[family] = p;
    return p;
  }

  function noop() {}

  function parse(el) {
    try { return JSON.parse(el.getAttribute('data-lp-cart') || '{}'); } catch (e) { return null; }
  }

  // Rejoin a word the capture had split in two.
  //
  // `lines` is drawn verbatim by this renderer, by the print stamper and by the
  // mockup — that agreement is the point, and it is also why one bad capture
  // becomes three identical wrong covers. Order 7673 recorded
  // ["Nef & Dim","","“","Our w","edding”"] on a cover the customer had seen
  // holding `“Our wedding”` whole.
  //
  // REPAIR, NOT DISCARD. Dropping `lines` leaves this renderer to re-wrap at the
  // descriptor's sizePct — and on 7673 the sizePct was corrupt the same way the
  // lines were, recording a size ABOVE the largest that fits, at which the
  // phrase is 16% too wide for the box. Re-wrapping gave `“Our` / `wedding”`,
  // which nobody saw either. Merging the impossible breaks keeps what the
  // capture got right and lets place()'s shrink-to-fit do the rest.
  //
  // Kept deliberately: a break after a dash or a slash, which any engine would
  // take. Returns {lines, healed} — healed is the list of closed breaks, or
  // null when the lines do not spell out the text and there is nothing to fix.
  var LP_BREAK_AFTER = /[-\u2010-\u2014\u002f\u005c]/;
  function bare(t) { return String(t == null ? '' : t).replace(/\s+/g, ''); }

  function repairLines(lines, content) {
    if (!Array.isArray(lines) || !lines.length) return { lines: lines, healed: [] };
    content = content || '';
    if (bare(lines.join('')) !== bare(content)) return { lines: null, healed: null };
    var bad = {}, i = 0, k, c;
    for (k = 0; k < lines.length - 1; k++) {
      var chars = bare(lines[k]);
      for (c = 0; c < chars.length; c++) {
        while (i < content.length && /\s/.test(content.charAt(i))) i++;
        i++;
      }
      if (!chars.length || !bare(lines[k + 1]).length) continue;
      var j = i;
      while (j < content.length && /\s/.test(content.charAt(j))) j++;
      if (j === i && !(i && LP_BREAK_AFTER.test(content.charAt(i - 1)))) bad[k + 1] = true;
    }
    var out = [], healed = [];
    for (k = 0; k < lines.length; k++) {
      if (bad[k] && out.length) {
        healed.push([String(out[out.length - 1]).slice(-8), String(lines[k]).slice(0, 8)]);
        out[out.length - 1] = String(out[out.length - 1]) + String(lines[k]);
      } else {
        out.push(String(lines[k]));
      }
    }
    return { lines: out, healed: healed };
  }
  // The order viewer draws the same cover and has to explain it to the merchant,
  // so the verdict is reachable rather than private — it must never warn about
  // something it is then quietly drawing anyway.
  window.LPCart = window.LPCart || {};
  window.LPCart.repairLines = repairLines;

  // Apply text + font styling to an overlay's .lp-cart-text child.
  function applyText(overlay) {
    var d = parse(overlay);
    var layer = d && d.layers && d.layers[0];
    if (!layer) return;
    var font = layer.font || {};
    var txt = overlay.querySelector('.lp-cart-text');
    if (!txt) { txt = document.createElement('div'); txt.className = 'lp-cart-text'; overlay.appendChild(txt); }
    // The browser recorded its own line breaks at add-to-cart. Re-joining them
    // with newlines, under white-space:pre-wrap, reproduces exactly those breaks
    // here too, at any width — the cart, the drawer and the order viewer are
    // all narrower than the PDP, so left to re-wrap they would disagree with
    // both the preview and the print.
    var fixed = repairLines(layer.lines, layer.content);
    var verbatim = !!(fixed.lines && fixed.lines.length);
    var content = verbatim ? fixed.lines.join('\n') : (layer.content || '');
    // Recorded lines are a TRANSCRIPT: they must never re-wrap, which is
    // exactly what `pre` guarantees and `pre-wrap` does not. The fit below
    // accepts a line up to 1px over the box, and under pre-wrap that 1px is
    // enough for the browser to break it — which is how the order viewer turned
    // «Our wedding» back into two lines after the repair had already put it
    // right. print draws the recorded lines verbatim too; this is the same rule.
    // Raw text (no recorded lines) is supposed to wrap, so it keeps pre-wrap.
    overlay.setAttribute('data-lp-verbatim', verbatim ? '1' : '');
    txt.style.whiteSpace = verbatim ? 'pre' : '';
    if (font.transform === 'uppercase') {
      try { content = content.toLocaleUpperCase('el-GR'); } catch (e) { content = content.toUpperCase(); }
    }
    txt.textContent = content;
    txt.style.color = font.color || '#222';
    txt.style.setProperty('font-family', font.family ? '"' + font.family + '", serif' : 'inherit', 'important');
    txt.style.lineHeight = font.leading || 1.15; // per-font leading from the descriptor
    txt.style.fontWeight = font.weight || 700;
    txt.style.textAlign = font.align || 'center';
    loadFont(font.family, font.url);
  }

  // Position the box (box %) then fit the text to it, capped by sizePct.
  function place(overlay) {
    var d = parse(overlay);
    var layer = d && d.layers && d.layers[0];
    if (!layer) return;
    var font = layer.font || {};
    var txt = overlay.querySelector('.lp-cart-text');
    if (!txt) return;
    var box = layer.box || { x: 10, y: 40, w: 80, h: 15 };
    overlay.style.left = box.x + '%';
    overlay.style.top = box.y + '%';
    overlay.style.width = box.w + '%';
    overlay.style.height = box.h + '%';
    var maxW = overlay.clientWidth;
    var maxH = overlay.clientHeight;
    if (maxH <= 0 || maxW <= 0) return;
    // Binary-search the largest size that fits BOTH axes, capped by the
    // descriptor's sizePct — the SAME algorithm as the PDP (live-preview.js
    // fitText). It must be able to GROW, not only shrink: an early fit can run
    // before the webfont lands, and a shrink-only loop could never recover from
    // the (much wider) fallback face once the real one arrived.
    var cap = Math.max(4, maxH * ((font.sizePct || 80) / 100));
    // Recorded lines are measured UNWRAPPED (applyText put them under `pre`).
    // Under pre-wrap the browser is free to break an over-wide line, so
    // scrollWidth fits at almost any size, the width limit never binds, and the
    // fit becomes height-only — the transcript silently gains lines. That is
    // what showed order 7673 as «Our» / «wedding» in the viewer while stamp.py,
    // which draws the lines verbatim and shrinks until the widest one fits,
    // printed the phrase whole.
    var fits = function (px) {
      txt.style.fontSize = px + 'px';
      return txt.scrollHeight <= maxH + 1 && txt.scrollWidth <= maxW + 1;
    };
    var lo = 4;
    var hi = cap;
    var best = 4;
    for (var i = 0; i < 20; i++) {
      var mid = (lo + hi) / 2;
      if (fits(mid)) { best = mid; lo = mid; } else { hi = mid; }
    }
    txt.style.fontSize = best + 'px';
    applyInkBleed(overlay, txt);
  }

  // The same measured ink bleed the PDP applies (live-preview.js inkBleed), for
  // the same reason: `.lp-cart-overlay` is `overflow: hidden`, so a glyph that
  // paints outside its line box is guillotined — and this is the surface the
  // customer checks their order on, plus the one the shop looks at before
  // printing. A cover that reads wrong here reads wrong at the worst moment.
  //
  // Measured, not declared. Astir's declared overrun is 0.20em and its real ink
  // overruns by 0.005em; Cavafy is declared safe and clips hardest of all, at
  // the TOP, on Ά Έ Ή Ί Ό Ύ Ώ; the true worst is Marilia at 0.335em below and
  // 0.67em to the left. clip-path takes negative insets, so the clip grows while
  // the box — and therefore the recorded line breaks — is left exactly alone.
  function applyInkBleed(overlay, txt) {
    var b = null;
    try { b = inkBleed(overlay, txt); } catch (e) { b = null; }
    if (!b || !(b.top || b.right || b.bottom || b.left)) {
      overlay.style.removeProperty('clip-path');
      overlay.style.removeProperty('overflow');
      return;
    }
    overlay.style.overflow = 'visible';
    overlay.style.clipPath = 'inset(' + (-b.top) + 'px ' + (-b.right) + 'px ' +
                             (-b.bottom) + 'px ' + (-b.left) + 'px)';
  }

  var inkCtx = null;
  function inkBleed(overlay, txt) {
    var none = { top: 0, right: 0, bottom: 0, left: 0 };
    var text = (txt && txt.textContent) || '';
    if (!text.trim()) return none;

    var cs = getComputedStyle(txt);
    var size = parseFloat(cs.fontSize) || 0;
    if (!size) return none;
    var lh = parseFloat(cs.lineHeight) || size;

    inkCtx = inkCtx || document.createElement('canvas').getContext('2d');
    if (!inkCtx) return none;
    inkCtx.font = (cs.fontStyle || 'normal') + ' ' + (cs.fontWeight || '400') + ' ' +
                  size + 'px ' + cs.fontFamily;

    var probe = inkCtx.measureText('Hg');
    var fAsc = probe.fontBoundingBoxAscent;
    var fDesc = probe.fontBoundingBoxDescent;
    if (!isFinite(fAsc) || !isFinite(fDesc)) return none;
    var baseline = (lh - (fAsc + fDesc)) / 2 + fAsc;

    var all = inkCtx.measureText(text.replace(/\n/g, ' '));
    var top = all.actualBoundingBoxAscent - baseline;
    var bottom = baseline + all.actualBoundingBoxDescent - lh;

    var left = 0, right = 0;
    var words = text.split(/\s+/);
    for (var i = 0; i < words.length; i++) {
      if (!words[i]) continue;
      var m = inkCtx.measureText(words[i]);
      if (m.actualBoundingBoxLeft > left) left = m.actualBoundingBoxLeft;
      if (m.actualBoundingBoxRight - m.width > right) right = m.actualBoundingBoxRight - m.width;
    }

    var capW = (overlay.clientWidth || size * 4) / 2;
    var capH = (overlay.clientHeight || size * 4) / 2;
    var clamp = function (v, cap) { return Math.min(Math.max(0, v || 0), cap); };
    return {
      top: clamp(top, capH), bottom: clamp(bottom, capH),
      left: clamp(left, capW), right: clamp(right, capW)
    };
  }

  function wire(overlay, img) {
    applyText(overlay);
    var run = function () { place(overlay); };
    if (img && !(img.complete && img.naturalWidth)) img.addEventListener('load', run, { once: true });
    run();
    var d = parse(overlay);
    var font = d && d.layers && d.layers[0] && d.layers[0].font;
    if (font && font.family) {
      // Re-fit once THIS face is actually usable...
      loadFont(font.family, font.url).then(run, run);
      // ...and again when the browser reports every pending face done, which
      // covers a face already in flight from another line item on the page.
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(run, noop);
    }
    // Re-fit once the overlay actually has a size (layout may lag the first run,
    // e.g. a cached/instant image, or scripts running before first layout) and on
    // any later resize. ResizeObserver isn't throttled like rAF; the setTimeout
    // fallbacks cover browsers without it and post-layout timing.
    if (window.ResizeObserver && !overlay.__lpRO) {
      overlay.__lpRO = new ResizeObserver(run);
      overlay.__lpRO.observe(overlay);
    }
    setTimeout(run, 0);
    setTimeout(run, 150);
  }

  function openModal(d) {
    if (!d || !d.base) return;
    var modal = document.createElement('div');
    modal.className = 'lp-cart-modal';
    var boxEl = document.createElement('div');
    boxEl.className = 'lp-cart-modal-box';
    var img = document.createElement('img');
    img.alt = 'preview';
    var ov = document.createElement('div');
    ov.className = 'lp-cart-overlay';
    ov.setAttribute('data-lp-cart', JSON.stringify(d));
    var txt = document.createElement('div');
    txt.className = 'lp-cart-text';
    ov.appendChild(txt);
    var close = document.createElement('button');
    close.type = 'button';
    close.className = 'lp-cart-modal-close';
    close.setAttribute('aria-label', 'Κλείσιμο');
    close.textContent = '×';
    boxEl.appendChild(img);
    boxEl.appendChild(ov);
    boxEl.appendChild(close);
    modal.appendChild(boxEl);
    document.body.appendChild(modal);
    wire(ov, img);
    img.src = d.base;
    var dismiss = function () { modal.remove(); document.removeEventListener('keydown', onKey); };
    modal.addEventListener('click', dismiss);
    boxEl.addEventListener('click', function (e) { if (e.target !== close) e.stopPropagation(); });
    function onKey(e) { if (e.key === 'Escape') dismiss(); }
    document.addEventListener('keydown', onKey);
  }

  function render(overlay) {
    if (overlay.__lpDone) return;
    var d = parse(overlay);
    if (!(d && d.layers && d.layers[0])) return;
    overlay.__lpDone = true;
    var container = overlay.parentElement;
    var img = container && container.querySelector('img');
    wire(overlay, img);
    // Click the preview to zoom (larger modal) — not on a suggestion's cover in the «Προστέθηκε» panel
    // (`data-lp-nozoom`, assets/cart-drawer.js), which is a link to its product.
    if (container && !container.__lpZoom && !overlay.hasAttribute('data-lp-nozoom')) {
      container.__lpZoom = true;
      container.classList.add('lp-cart-zoom');
      container.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        openModal(parse(overlay));
      });
    }
  }

  function scan() {
    injectStyle();
    var list = document.querySelectorAll('.lp-cart-overlay[data-lp-cart]');
    for (var i = 0; i < list.length; i++) render(list[i]);
  }
  // For a descriptor written onto an overlay that is already in the page (an attribute change is not one the
  // observer below sees): the «Προστέθηκε» panel's suggestion covers, assets/cart-drawer.js.
  window.LPCart.render = function (overlay) { injectStyle(); if (overlay) render(overlay); };

  function start() {
    scan();
    if (window.__lpCartMO) return;
    // Debounce via a microtask (not requestAnimationFrame — rAF is paused in
    // headless/background tabs, which would drop drawer/notification renders).
    var pending = false;
    window.__lpCartMO = new MutationObserver(function () {
      if (pending) return;
      pending = true;
      Promise.resolve().then(function () { pending = false; scan(); });
    });
    // Watch the cart containers Dawn re-renders (drawer/notification/cart page).
    var sels = ['cart-notification', 'cart-drawer', '#cart-notification-product', '#main-cart-items', '.cart-items', '#cart'];
    var roots = [];
    for (var i = 0; i < sels.length; i++) {
      document.querySelectorAll(sels[i]).forEach(function (n) { roots.push(n); });
    }
    if (!roots.length) roots.push(document.body);
    roots.forEach(function (r) { window.__lpCartMO.observe(r, { childList: true, subtree: true }); });
    window.addEventListener('resize', function () {
      document.querySelectorAll('.lp-cart-overlay[data-lp-cart]').forEach(place);
    });
  }

  if (document.readyState !== 'loading') start();
  else document.addEventListener('DOMContentLoaded', start);
})();
